/**
 * Device detection utility for responsive behavior
 */

export type DeviceType = 'phone' | 'tablet' | 'desktop';

export interface DeviceInfo {
  type: DeviceType;
  isTouch: boolean;
  screenWidth: number;
  screenHeight: number;
}

export interface SwipeConfig {
  delta: number;           // Minimum distance for swipe
  enabled: boolean;        // Whether swipe is enabled
  rotationAngle: number;   // Maximum angle from horizontal
  swipeDuration: number;   // Maximum duration for swipe gesture
}

/**
 * Detect the current device type based on screen size and user agent
 */
export function detectDevice(): DeviceInfo {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  let type: DeviceType;

  if (width < 768) {
    type = 'phone';
  } else if (width >= 768 && width < 1024) {
    type = 'tablet';
  } else {
    type = 'desktop';
  }

  // Additional check for large phones in landscape
  if (type === 'tablet' && height < 600) {
    type = 'phone'; // Likely a phone in landscape
  }

  return {
    type,
    isTouch,
    screenWidth: width,
    screenHeight: height,
  };
}

/**
 * Get recommended swipe configuration based on device type
 */
export function getSwipeConfig(deviceType: DeviceType, userPreference?: boolean): SwipeConfig {
  // If user has explicitly disabled swipes, respect that
  if (userPreference === false) {
    return {
      delta: Infinity, // Effectively disables swipes
      enabled: false,
      rotationAngle: 0,
      swipeDuration: 0,
    };
  }

  switch (deviceType) {
    case 'phone':
      return {
        delta: 75,           // Lower threshold for phones
        enabled: true,
        rotationAngle: 45,   // Allow diagonal swipes
        swipeDuration: 1000, // 1 second max
      };

    case 'tablet':
      return {
        delta: 150,          // Higher threshold for tablets (2x phone)
        enabled: true,       // Enabled by default but with stricter requirements
        rotationAngle: 30,   // More horizontal swipes required
        swipeDuration: 800,  // Slightly faster swipe required
      };

    case 'desktop':
      return {
        delta: Infinity,     // Disable swipes on desktop
        enabled: false,
        rotationAngle: 0,
        swipeDuration: 0,
      };

    default:
      // Fallback to tablet settings for unknown devices
      return getSwipeConfig('tablet');
  }
}

/**
 * Check if user is likely scrolling vertically
 */
export function isVerticalScroll(deltaX: number, deltaY: number): boolean {
  // If vertical movement is more than horizontal, it's likely a scroll
  return Math.abs(deltaY) > Math.abs(deltaX) * 1.5; // 1.5x threshold
}

/**
 * Check if the user is on an iPad specifically
 * iPads often report as desktop in viewport but are actually tablets
 */
export function isIPad(): boolean {
  return /iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Get device-specific message for swipe hints
 */
export function getSwipeHintMessage(deviceType: DeviceType): string {
  switch (deviceType) {
    case 'phone':
      return 'Swipe left or right to navigate between sections';
    case 'tablet':
      return 'Swipe firmly left or right to change sections';
    case 'desktop':
      return 'Use the navigation buttons or keyboard arrows to navigate';
    default:
      return 'Navigate between sections using the buttons';
  }
}

/**
 * Store user's swipe preference in localStorage
 */
export function setSwipePreference(enabled: boolean): void {
  localStorage.setItem('siteSurvey.swipeEnabled', enabled.toString());
}

/**
 * Get user's swipe preference from localStorage
 */
export function getSwipePreference(): boolean | null {
  const stored = localStorage.getItem('siteSurvey.swipeEnabled');
  if (stored === null) return null;
  return stored === 'true';
}

/**
 * Check if swipe navigation should be enabled based on device and user preference
 */
export function shouldEnableSwipe(): boolean {
  const device = detectDevice();
  const userPreference = getSwipePreference();

  // If user has set a preference, respect it
  if (userPreference !== null) {
    return userPreference;
  }

  // Default behavior based on device
  switch (device.type) {
    case 'phone':
      return true;  // Enabled by default on phones
    case 'tablet':
      // Check if it's an iPad, which might need special handling
      if (isIPad()) {
        return false; // Disabled by default on iPads due to gesture conflicts
      }
      return false; // Disabled by default on tablets
    case 'desktop':
      return false; // Always disabled on desktop
    default:
      return false;
  }
}